import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { OpDivider } from 'new-components/DLS/OpDivider/OpDivider';
import { OpTitle } from 'new-components/DLS/OpTitle/OpTitle';
import { OnSubmitArgs, OpForm } from 'new-components/DLS/OpForm/OpForm';
import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useSelectorJs } from 'utils/customHooks';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useEndpointPermissions } from 'utils/customHooks/useEndpointPermissions';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive, heliumSend } from 'utils/helpers';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { Loader } from 'components/Loader';

import { AppDispatch, ImmutableRootState } from '../../../../store/store';
import {
  searchVisitors,
  searchVisitorsToday,
  VisitorSearchState,
} from '../../../../store/slices/visitorSearchSlice';
import { fetchVisits, VisitsState } from '../../../../store/slices/visitsSlice';
import { SIGNED_IN_VISITOR_ID } from '../../../../constants/userActivities';
import { Visitor } from '../../../../types/visitTypes';
import { FormFieldWorkflow, VisitData } from '../../../../types/formFieldTypes';
import { formatFullName } from '../../../../utils/utils';
import {
  CustomFieldsWorkflowResponse,
  ExcludeHostsResponse,
  HostUser,
  LocationWorkflowResponse,
  UsersResponse,
  VisitDataResponse,
  VisitorTypeWorkflowResponse,
} from '../../../../types/visitorsDrawerTypes';

interface RequiredFieldsFormValues {
  company?: string;
  hostId?: number;
  visitPurpose?: string;
  [key: string]: unknown;
}

interface RequiredFieldsModalProps {
  open: boolean;
  requiredFields: string[];
  checkedVisitors: Visitor[];
  onClose: () => void;
  updateVisitStatus: () => void;
  updateVisitorStatus: (visitorId: number) => void;
  setLoading: (loading: boolean) => void;
}

const RequiredFieldsModal = ({
  open,
  requiredFields,
  checkedVisitors,
  onClose,
  updateVisitStatus,
  updateVisitorStatus,
  setLoading,
}: RequiredFieldsModalProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const { userId: globalUserId } = useIdentityAndUser();
  const { isValid: hasAllVisitorsWrite } = useValidateScopes([
    'o{orgId}-visitorAllVisitors:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const { hasReadPermission: hasUsersRead } =
    useEndpointPermissions('listUsers');
  const { hasReadPermission: hasExcludeHostsRead } = useEndpointPermissions(
    'listAllExcludeHosts',
  );

  const globalLocationId = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'globalLocation', 'id']),
  ) as number;
  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];
  const lastSearchParams = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorSearch', 'lastSearchParams']),
  ) as VisitorSearchState['lastSearchParams'];

  const [hosts, setHosts] = useState<HostUser[]>([]);
  const [excludedHostIds, setExcludedHostIds] = useState<number[]>([]);
  const [visitData, setVisitData] = useState<VisitData[]>([]);
  const [fieldsWithoutValue, setFieldsWithoutValue] = useState<
    FormFieldWorkflow[]
  >([]);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [form] = OpForm.useForm();

  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true);
      try {
        let activeUsers: HostUser[] = [];
        let excludedIds: number[] = [];

        if (hasUsersRead && hasExcludeHostsRead) {
          const [usersRes, excludedHostsRes] = (await Promise.all([
            heliumReceive('listUsers', [orgId]),
            heliumReceive('listAllExcludeHosts', [orgId]),
          ])) as [UsersResponse, ExcludeHostsResponse];

          activeUsers = (usersRes.data ?? []).filter(
            (user) => user.status === 'A',
          );
          excludedIds = (excludedHostsRes.data ?? []).map((ex) => ex.userId);
        } else {
          if (hasUsersRead) {
            const usersRes = (await heliumReceive('listUsers', [
              orgId,
            ])) as UsersResponse;
            activeUsers = (usersRes.data ?? []).filter(
              (user) => user.status === 'A',
            );
          }
          if (hasExcludeHostsRead) {
            const excludedHostsRes = (await heliumReceive(
              'listAllExcludeHosts',
              [orgId],
            )) as ExcludeHostsResponse;
            excludedIds = (excludedHostsRes.data ?? []).map((ex) => ex.userId);
          }
        }

        const locationDefaultWorkflow = (await heliumReceive('locationConfig', [
          orgId,
          selectedVisit?.location?.id,
        ])) as LocationWorkflowResponse;
        const locationVisitorTypeWorkflow = (await heliumReceive(
          'describeVisitorTypeWorkflow',
          [orgId, selectedVisit?.location.id, selectedVisit?.visitorType.id],
        )) as VisitorTypeWorkflowResponse;

        const defaultWorkflowData = locationDefaultWorkflow?.data?.[0];
        const visitorTypeWorkflowData =
          locationVisitorTypeWorkflow?.data?.[0]?.workflow;
        const visitorTypeSignInWorkflowId =
          visitorTypeWorkflowData?.SignInWorkflowId;
        const signInWorkflowId =
          visitorTypeSignInWorkflowId !== undefined &&
          visitorTypeSignInWorkflowId !== -1
            ? visitorTypeSignInWorkflowId
            : defaultWorkflowData?.SignInWorkflowId;

        const customFieldsWorkflow = (await heliumReceive(
          'getVisitorWorkflowFieldsCustom',
          [orgId, signInWorkflowId],
        )) as CustomFieldsWorkflowResponse;
        const visitDataResponse = (await heliumReceive('getVisitorData', [
          orgId,
          selectedVisit?.id,
        ])) as VisitDataResponse;

        // Filter fields without values
        const fields = (customFieldsWorkflow.data ?? []).filter(
          (formField: FormFieldWorkflow) => {
            const matchingVisitData = (visitDataResponse.data ?? []).find(
              (data: VisitData) =>
                data.formField.id === formField.id &&
                data.value !== null &&
                data.value !== '',
            );
            return formField.required === 1 && !matchingVisitData; // Keep only required fields without values
          },
        );

        setHosts(activeUsers);
        setExcludedHostIds(excludedIds);
        setVisitData(visitDataResponse.data ?? []);
        setFieldsWithoutValue(fields);
      } catch {
        // Failed to fetch data
      } finally {
        setDataLoading(false);
      }
    };
    fetchData();
  }, [
    orgId,
    selectedVisit?.id,
    selectedVisit?.location.id,
    selectedVisit?.visitorType.id,
    hasUsersRead,
    hasExcludeHostsRead,
  ]);

  const handleOk = async () => {
    form
      .validateFields()
      .then(() => {
        setSubmitting(true);
        form.submit();
      })
      .catch(() => {
        notificationService.show('error', t('notification.validationError'));
      });
  };

  const renderFormFields = () => {
    return (
      <>
        {fieldsWithoutValue.map((formField) => {
          if (formField.values) {
            const options = formField.values.map((option) => ({
              label: option.display || option.name,
              value: option.value,
            }));

            return (
              <OpForm.Select
                key={formField.id}
                label={formField.name}
                name={formField.id}
                options={options}
                rules={[{ required: formField.required === 1 }]}
                placeholder={
                  formField.placeholder ? formField.placeholder : undefined
                }
              />
            );
          }
          return (
            <OpForm.TextAreaInput
              key={formField.id}
              label={formField.name}
              name={formField.id}
              rows={4}
              rules={[{ required: formField.required === 1 }]}
              placeholder={
                formField.placeholder ? formField.placeholder : undefined
              }
            />
          );
        })}
      </>
    );
  };

  const handleSubmit = useCallback(
    async ({ values }: OnSubmitArgs<RequiredFieldsFormValues>) => {
      setLoading(true);
      try {
        const visitPayload: Record<string, string | number> = {};
        if (values.company) visitPayload.company = values.company as string;
        if (values.hostId) visitPayload.hostId = values.hostId as number;
        if (values.visitPurpose) {
          visitPayload.purpose = values.visitPurpose as string;
        }

        await heliumSend(
          'updateVisit',
          [orgId, selectedVisit?.id],
          visitPayload,
        );

        const visitorPayloads: Record<string, Record<string, string>> = {};

        Object.entries(values).forEach(([key, value]) => {
          if (key.startsWith('visitor_') && value) {
            const [, visitorId, field] = key.split('_');
            if (!visitorPayloads[visitorId]) visitorPayloads[visitorId] = {};
            visitorPayloads[visitorId][field] = value as string;
          }
        });

        const visitorRequests = Object.entries(visitorPayloads).map(
          ([visitorId, payload]) =>
            heliumSend(
              'updateVisitors',
              [orgId, selectedVisit?.id, visitorId],
              payload,
            ),
        );

        const customFieldRequests = Object.entries(values)
          .filter(([key]) => !isNaN(Number(key)))
          .map(async ([key, value]) => {
            const existingField = visitData.find(
              (d) => Number(d.formField.id) === Number(key),
            );
            try {
              if (existingField) {
                return await heliumSend(
                  'updateVisitorData',
                  [orgId, selectedVisit?.id, existingField.id],
                  {
                    fieldId: key,
                    value,
                  },
                );
              }
              return await heliumSend(
                'createVisitorData',
                [orgId, selectedVisit?.id],
                {
                  fieldId: key,
                  value,
                },
              );
            } catch {
              // Failed to handle custom field
            }
          });

        const statusRequests = checkedVisitors
          .filter((v) => v.id)
          .map((v) => updateVisitorStatus(v.id!));

        await Promise.all([
          ...visitorRequests,
          ...customFieldRequests,
          ...statusRequests,
        ]);

        await updateVisitStatus();

        const fullNameList = checkedVisitors
          ?.map((v) => formatFullName(v.firstName, v.middleName, v.lastName))
          .join(', ');

        if (!globalUserId) {
          notificationService.show(
            'warning',
            t('notification.activityLogMasterModeWarning'),
          );
        } else {
          await heliumSend('newActivityLog', [orgId], {
            userId: globalUserId,
            activityId: SIGNED_IN_VISITOR_ID,
            details: fullNameList,
          });
        }

        dispatch(fetchVisits({ orgId }));
        if (Object.keys(lastSearchParams).length !== 0) {
          dispatch(
            searchVisitors({
              ...lastSearchParams,
              orgId,
              locationId: lastSearchParams.locationId || globalLocationId!,
            }),
          );
        }
        dispatch(searchVisitorsToday({ orgId }));

        form.resetFields();
        onClose();
      } catch {
        notificationService.show('error', t('notification.submitError'));
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    },
    [
      orgId,
      selectedVisit,
      checkedVisitors,
      dispatch,
      form,
      globalUserId,
      globalLocationId,
      lastSearchParams,
      visitData,
      updateVisitStatus,
      updateVisitorStatus,
      onClose,
      t,
      setLoading,
    ],
  );

  const filteredHosts = (
    hasAllVisitorsWrite
      ? hosts
      : hosts.filter((host) => host.id === globalUserId)
  ).filter(
    (host) =>
      !excludedHostIds.includes(host.id) ||
      host.id === selectedVisit?.host?.userId,
  );

  const shouldShowVisitInformationTitle =
    (requiredFields.includes('visitCompany') && !selectedVisit?.company) ||
    ((requiredFields.includes('host') || filteredHosts.length > 0) &&
      !selectedVisit?.host) ||
    (requiredFields.includes('visitPurpose') && !selectedVisit?.purpose);

  const hasVisitOrVisitorInfo =
    shouldShowVisitInformationTitle ||
    checkedVisitors.some(
      (visitor) =>
        (requiredFields.includes('middleName') && !visitor.middleName) ||
        (requiredFields.includes('email') && !visitor.email) ||
        (requiredFields.includes('visitPhone') && !visitor.mobilePhone),
    );

  return (
    <OpModal
      open={open}
      title={t('visitors.requiredFieldsModal.title')}
      onOk={handleOk}
      onCancel={onClose}
      centered
      width={720}
      loading={dataLoading}
      confirmLoading={submitting}
    >
      {dataLoading ? (
        <Loader />
      ) : (
        <OpForm
          form={form}
          onSubmit={handleSubmit}
          defaultButtons={false}
          hasError={false}
          style={{ maxHeight: '70vh', overflowY: 'auto', padding: '8px' }}
        >
          {shouldShowVisitInformationTitle && (
            <OpTitle level={5} style={{ marginBottom: 16 }}>
              {t('visitors.requiredFieldsModal.visitInfo')}
            </OpTitle>
          )}
          {requiredFields.includes('visitCompany') &&
            !selectedVisit?.company && (
              <OpForm.Input
                label={t('visitors.requiredFieldsModal.company')}
                name="company"
                rules={[
                  {
                    required: true,
                    message: t('visitors.requiredFieldsModal.companyRequired'),
                  },
                ]}
              />
            )}
          {requiredFields.includes('host') &&
            filteredHosts.length > 0 &&
            !selectedVisit?.host?.userId && (
              <OpForm.Select
                label={t('visitors.requiredFieldsModal.host')}
                name="hostId"
                rules={[
                  {
                    required: true,
                    message: t('visitors.requiredFieldsModal.hostRequired'),
                  },
                ]}
                placeholder={
                  filteredHosts.length === 0
                    ? t(
                        'visitors.requiredFieldsModal.hostPlaceholderNotAvailable',
                      )
                    : t('visitors.requiredFieldsModal.hostPlaceholder')
                }
                showSearch={{
                  filterOption: (input, option) =>
                    (option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase()),
                }}
                options={filteredHosts.map((host) => ({
                  label: host.identity?.fullName || host.identity?.email,
                  value: host.id,
                }))}
                allowClear={hasAllVisitorsWrite}
              />
            )}
          {requiredFields.includes('visitPurpose') &&
            !selectedVisit?.purpose && (
              <OpForm.TextAreaInput
                label={t('visitors.requiredFieldsModal.purpose')}
                name="visitPurpose"
                rules={[
                  {
                    required: true,
                    message: t('visitors.requiredFieldsModal.purposeRequired'),
                  },
                ]}
                rows={4}
              />
            )}
          {checkedVisitors.some(
            (visitor) =>
              (requiredFields.includes('middleName') && !visitor.middleName) ||
              (requiredFields.includes('email') && !visitor.email) ||
              (requiredFields.includes('visitPhone') && !visitor.mobilePhone),
          ) &&
            checkedVisitors.map((visitor, index) => (
              <div key={visitor.id} style={{ marginBottom: 24 }}>
                {shouldShowVisitInformationTitle || index !== 0 ? (
                  <OpDivider />
                ) : null}
                <OpTitle level={5} style={{ marginBottom: 16 }}>
                  {formatFullName(
                    visitor.firstName,
                    visitor.middleName,
                    visitor.lastName,
                  )}
                </OpTitle>
                {requiredFields.includes('middleName') &&
                  !visitor.middleName && (
                    <OpForm.Input
                      label={t('visitors.requiredFieldsModal.middleName')}
                      name={`visitor_${visitor.id}_middleName`}
                      rules={[
                        {
                          required: true,
                          message: t(
                            'visitors.requiredFieldsModal.middleNameRequired',
                          ),
                        },
                      ]}
                    />
                  )}
                {requiredFields.includes('email') && !visitor.email && (
                  <OpForm.Input
                    label={t('visitors.requiredFieldsModal.email')}
                    name={`visitor_${visitor.id}_email`}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'visitors.requiredFieldsModal.emailRequired',
                        ),
                      },
                      {
                        type: 'email',
                        message: t('visitors.requiredFieldsModal.emailInvalid'),
                      },
                    ]}
                  />
                )}
                {requiredFields.includes('visitPhone') &&
                  !visitor.mobilePhone && (
                    <OpForm.PhoneInput
                      label={t('visitors.requiredFieldsModal.phone')}
                      name={`visitor_${visitor.id}_mobilePhone`}
                      rules={[
                        {
                          required: true,
                          message: t(
                            'visitors.requiredFieldsModal.phoneRequired',
                          ),
                        },
                      ]}
                    />
                  )}
              </div>
            ))}

          {fieldsWithoutValue.length > 0 && hasVisitOrVisitorInfo && (
            <OpDivider />
          )}
          {fieldsWithoutValue.length > 0 && (
            <OpTitle level={5} style={{ marginBottom: 16 }}>
              {t('visitors.requiredFieldsModal.customFields')}
            </OpTitle>
          )}
          {renderFormFields()}
        </OpForm>
      )}
    </OpModal>
  );
};

export default RequiredFieldsModal;
